import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Heart, HandHeart, MessageCircle, Package, TrendingUp, TrendingDown, FileText, ArrowRight, Medal, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, color, link }) => (
  <Link to={link || '#'}>
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl shadow-sm border border-stone-200 p-5 card-lift cursor-pointer`}>
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center mb-3`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-2xl font-heading font-bold text-stone-900">{value}</p>
      <p className="text-sm text-stone-500">{label}</p>
    </motion.div>
  </Link>
);

const MyDashboard = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const it = language === 'it';

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/users/me/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch {} finally { setLoading(false); }
    };
    fetchDashboard();
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-stone-100">
      <Navbar />
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="my-dashboard">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 mb-1">
            {it ? 'La mia attività' : 'My Activity'}
          </h1>
          <p className="text-stone-500 mb-8">{it ? 'Panoramica completa del tuo account' : 'Complete overview of your account'}</p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={TrendingUp} label={it ? 'Donato' : 'Donated'}
            value={`€${data?.total_donated?.toFixed(0) || 0}`}
            color="bg-emerald-50 text-emerald-700" link="/leaderboard" />
          <StatCard icon={TrendingDown} label={it ? 'Ricevuto' : 'Received'}
            value={`€${data?.total_received?.toFixed(0) || 0}`}
            color="bg-amber-50 text-amber-700" link="/feed" />
          <StatCard icon={FileText} label={it ? 'I miei post' : 'My posts'}
            value={data?.posts_count || 0}
            color="bg-blue-50 text-blue-700" link="/feed" />
          <StatCard icon={Package} label={it ? 'Annunci mercatino' : 'Marketplace listings'}
            value={data?.marketplace_count || 0}
            color="bg-teal-50 text-teal-700" link="/marketplace" />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* My Posts */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold text-stone-900">{it ? 'Le mie richieste' : 'My requests'}</h2>
              <Link to="/create-post">
                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full text-xs px-4">
                  {it ? '+ Nuova' : '+ New'}
                </Button>
              </Link>
            </div>
            {data?.posts?.length === 0 ? (
              <p className="text-stone-400 text-sm py-6 text-center">{it ? 'Nessuna richiesta ancora' : 'No requests yet'}</p>
            ) : (
              <div className="space-y-3">
                {data?.posts?.map(post => {
                  const progress = post.amount_needed > 0 ? Math.min((post.amount_raised / post.amount_needed) * 100, 100) : 0;
                  return (
                    <Link key={post.post_id} to={`/posts/${post.post_id}`}
                      className="block p-3 rounded-xl hover:bg-stone-50 transition-colors border border-stone-100">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="font-medium text-stone-900 text-sm truncate flex-1 mr-2">{post.title}</p>
                        <Badge variant="secondary" className="text-[10px]">{post.status}</Badge>
                      </div>
                      <div className="flex items-center gap-3">
                        <Progress value={progress} className="flex-1 h-2" />
                        <span className="text-xs text-stone-500 shrink-0">{progress.toFixed(0)}%</span>
                      </div>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-xs text-stone-400">{post.donors_count || 0} {it ? 'donatori' : 'donors'}</span>
                        <span className="text-xs font-semibold text-amber-600">€{post.amount_raised?.toFixed(0) || 0} / €{post.amount_needed?.toFixed(0)}</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Marketplace Items */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold text-stone-900">{it ? 'I miei annunci' : 'My listings'}</h2>
              <Link to="/marketplace">
                <Button size="sm" variant="outline" className="rounded-full text-xs px-4">
                  {it ? 'Vai al mercatino' : 'Go to marketplace'} <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </div>
            {data?.marketplace_items?.length === 0 ? (
              <p className="text-stone-400 text-sm py-6 text-center">{it ? 'Nessun annuncio' : 'No listings'}</p>
            ) : (
              <div className="space-y-3">
                {data?.marketplace_items?.map(item => (
                  <div key={item.item_id} className="p-3 rounded-xl border border-stone-100 flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${item.type === 'offer' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                      {item.type === 'offer' ? <Gift className="h-4 w-4 text-emerald-600" /> : <HandHeart className="h-4 w-4 text-amber-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900 text-sm truncate">{item.title}</p>
                      <p className="text-xs text-stone-400">{item.category}</p>
                    </div>
                    <Badge className={item.type === 'offer' ? 'bg-emerald-100 text-emerald-700 text-[10px]' : 'bg-amber-100 text-amber-700 text-[10px]'}>
                      {item.type === 'offer' ? (it ? 'Regalo' : 'Gift') : (it ? 'Cerco' : 'Request')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Donations Made */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
            <h2 className="font-heading text-lg font-semibold text-stone-900 mb-4">{it ? 'Donazioni effettuate' : 'Donations made'}</h2>
            {data?.donations_made?.length === 0 ? (
              <p className="text-stone-400 text-sm py-6 text-center">{it ? 'Nessuna donazione' : 'No donations'}</p>
            ) : (
              <div className="space-y-2">
                {data?.donations_made?.map((d, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-stone-50">
                    <div className="flex items-center gap-2">
                      <Heart className="h-4 w-4 text-amber-500" fill="currentColor" />
                      <span className="text-sm text-stone-700">{new Date(d.created_at).toLocaleDateString(it ? 'it-IT' : 'en-US', { day: 'numeric', month: 'short' })}</span>
                    </div>
                    <span className="text-sm font-semibold text-emerald-700">€{d.amount?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Donations Received */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
            <h2 className="font-heading text-lg font-semibold text-stone-900 mb-4">{it ? 'Donazioni ricevute' : 'Donations received'}</h2>
            {data?.donations_received?.length === 0 ? (
              <p className="text-stone-400 text-sm py-6 text-center">{it ? 'Nessuna donazione ricevuta' : 'No donations received'}</p>
            ) : (
              <div className="space-y-2">
                {data?.donations_received?.map((d, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-stone-50">
                    <div className="flex items-center gap-2">
                      <HandHeart className="h-4 w-4 text-emerald-500" />
                      <span className="text-sm text-stone-700">{d.donor_name || 'Anonimo'}</span>
                    </div>
                    <span className="text-sm font-semibold text-amber-600">€{d.amount?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MyDashboard;
